import { StreamChat } from 'stream-chat';
import { useDispatch, useSelector } from 'react-redux';
import { useRef, MutableRefObject } from 'react';
import { channelsType } from '@/typings/allType';
import { setUnReadCount } from '@/store/stateSlice';
import chatApi from '@/api/chat';
import {
    createDistinctWrappedChannel,
    findWrappedChannelForUser,
    queryWrappedChannels,
} from '@/lib/streamChannelAdapter';
import { ensureStreamUserConnected } from '@/lib/streamChatSession';

const useStreamChat = () => {
    const { adminId, streamApiKey, chatAccessToken } = useSelector((state: any) => state.state.userInfo);
    const { chatUserId } = useSelector((state: any) => state.state.chatUser);
    const clientRef: MutableRefObject<StreamChat | null> = useRef(null);
    const dispatch = useDispatch();

    const getClient = () => {
        if (!streamApiKey) {
            return null;
        }
        return StreamChat.getInstance(streamApiKey);
    };

    const getUNReadCount = async () => {
        const client = clientRef.current;
        if (!client) {
            return;
        }
        const count = await client.getUnreadCount();
        dispatch(setUnReadCount(count?.total_unread_count || 0));
    };

    const loginChat = async (callback: (channels: channelsType[]) => void) => {
        if (!chatAccessToken || !streamApiKey || !adminId) {
            callback([]);
            return;
        }
        const streamUserId = String(adminId);
        const client = getClient();
        if (!client) {
            callback([]);
            return;
        }
        clientRef.current = client;
        try {
            await ensureStreamUserConnected(client, streamUserId, chatAccessToken);
            await chatApi.syncSupportChannels().catch(() => undefined);
            const channels = await queryWrappedChannels(client, streamUserId);
            callback(channels as unknown as channelsType[]);
            await getUNReadCount();
        } catch {
            callback([]);
        }
    };

    const initStreamChat = (callback: (channels: channelsType[]) => void) => {
        loginChat(callback).catch(() => callback([]));
    };

    const createChannel = async (userIdsToInvite: string[], callback: (channel: channelsType) => void) => {
        try {
            const client = getClient();
            if (!client || !adminId) {
                callback({} as channelsType);
                return;
            }
            const streamUserId = String(adminId);
            const targetUserId = userIdsToInvite.map(id => String(id)).find(id => id !== streamUserId);
            if (targetUserId) {
                const existing = await findWrappedChannelForUser(client, streamUserId, targetUserId);
                if (existing) {
                    callback(existing as unknown as channelsType);
                    return;
                }
            }
            const channel = await createDistinctWrappedChannel(client, [
                ...userIdsToInvite.map(id => String(id)),
                streamUserId,
            ]);
            callback(channel as unknown as channelsType);
        } catch {
            callback({} as channelsType);
        }
    };

    const filterChatUserId = (
        channels: channelsType[],
        targetChatUserId: string,
        message: string,
        callback: (result: string) => void
    ) => {
        const channel = channels.filter(item => {
            return item.members.findIndex(member => member.userId === targetChatUserId) !== -1;
        })[0];
        if (channel) {
            channel
                .sendUserMessage({ message })
                .onSucceeded(() => callback('onSucceeded'))
                .onFailed(() => callback('onFailed'));
            return;
        }
        createChannel([targetChatUserId, adminId], () => callback('onFailed'));
    };

    const sendMsg = (taskerId: string, message: string, callback: (result: string) => void) => {
        initStreamChat(list => {
            filterChatUserId(list, chatUserId || taskerId, message, callback);
        });
    };

    return {
        initStreamChat,
        createChannel,
        sendMsg,
        refreshUnreadCount: getUNReadCount,
    };
};

export default useStreamChat;
